// The Compliance copy seam (SEP-243 decision D4, sections 9, 10 and 11).
//
// SEP-243 marks three items of wording TBC with Compliance: the high net worth
// definition (section 10), the conflict categories (section 11), and the
// client-classification explanations (section 10). Decision D4 says to build the
// CAPABILITY and to put the words behind a configuration seam, so Compliance
// changes them with no change to the code.
//
// This module is the READ half of the seam, and it is PURE. It holds the closed
// list of items, the placeholder wording, and the one fold that resolves an item
// against the published register. `ComplianceCopyEntry` in the ontology is the
// WRITE half, and `PublishComplianceCopy` supersedes rather than edits.
//
// THE PLACEHOLDER IS CODE, AND IT IS NOT A ROW. The register holds no row until
// Compliance publishes one. Two facts make that the right shape. A row in the
// register reads as approved wording, so a seeded placeholder row would read as
// approved wording that nobody approved. And the platform seeds no domain data
// (ADR-0009).
//
// Every surface reads `resolveComplianceCopy`, so no surface can present a
// placeholder as firm copy: the result carries `isPlaceholder`, and the
// placeholder body names itself.
//
// SEP-256 ADDS THE CONFIRMED ITEM. The firm gave its own words for the
// conflict definition and for the high net worth definition. Those words are
// not a placeholder, and a pill that reads "Awaiting Compliance" beside them
// would state something false. `confirmedByFirm` marks such an item, and the
// read gives `isPlaceholder: false` for it. Compliance keeps the last word,
// because a published entry always wins over the wording in the code.

import { z } from 'zod';

/** The areas of wording that Compliance owns today. */
export const complianceCopyScopeSchema = z.enum([
    'hnw',
    'conflict',
    'classification',
    'declaration',
    // SEP-263 decision D3. The footer of every page of the supplied DGW
    // Application Form (Disc/Adv) Individual v2.0 reads "DRAFT (pending
    // compliance sign-off)". The client application therefore ships its
    // structure now, and Compliance owns the words.
    'application',
]);
export type ComplianceCopyScope = z.infer<typeof complianceCopyScopeSchema>;

/** One published item of Compliance-owned wording, on the wire. */
export const complianceCopyEntrySchema = z.object({
    id: z.string(),
    scope: complianceCopyScopeSchema,
    code: z.string(),
    title: z.string(),
    body: z.string(),
    options: z.array(z.string()).nullable(),
    activeFrom: z.string(),
    supersededAt: z.string().nullable(),
    supersededByEntryId: z.string().nullable(),
    publishedByUserId: z.string().nullable(),
    publishNote: z.string().nullable(),
});
export type ComplianceCopyEntryDto = z.infer<typeof complianceCopyEntrySchema>;

/**
 * The result of one resolved item.
 *
 * `entryId` is null when the placeholder governs the item. A record that pins
 * the id therefore stores null, which is the truthful statement that Compliance
 * had published nothing when somebody wrote the record.
 */
export const resolvedComplianceCopySchema = z.object({
    scope: complianceCopyScopeSchema,
    code: z.string(),
    title: z.string(),
    body: z.string(),
    options: z.array(z.string()),
    /** The published entry that governs the item, or null for the placeholder. */
    entryId: z.string().nullable(),
    activeFrom: z.string().nullable(),
    /**
     * True while Compliance has published nothing for the item, and the firm
     * has not confirmed the wording that the platform ships.
     */
    isPlaceholder: z.boolean(),
});
export type ResolvedComplianceCopy = z.infer<typeof resolvedComplianceCopySchema>;

/** One item that the seam carries, with the wording the platform ships today. */
export interface ComplianceCopyItem {
    readonly scope: ComplianceCopyScope;
    readonly code: string;
    readonly title: string;
    /**
     * True when the firm confirmed these words.
     *
     * The provided-document catalogue carries the same word for the same idea.
     * The body of a confirmed item is the wording of the firm, and it is not a
     * placeholder. The body of an unconfirmed item opens with
     * `COMPLIANCE_COPY_PLACEHOLDER_PREFIX`, so it names itself.
     */
    readonly confirmedByFirm: boolean;
    /** The wording that the platform ships for the item. */
    readonly body: string;
    /** The option list, where the item IS a list. */
    readonly options: ReadonlyArray<string>;
}

/**
 * The sentence that opens every placeholder body.
 *
 * It is one constant, so a reader of the code and a reader of the screen see
 * the same words, and a test can assert that every placeholder carries it.
 */
export const COMPLIANCE_COPY_PLACEHOLDER_PREFIX =
    'This wording is a draft. Compliance has not approved it yet.';

/**
 * The closed set of items the seam carries today.
 *
 * A new item is a new entry here plus a `code` that the writing surface can
 * publish. Nothing else changes.
 *
 * An item that the firm confirmed carries the words of the firm. An item that
 * the firm has not confirmed carries a placeholder that names itself.
 */
export const COMPLIANCE_COPY_ITEMS: ReadonlyArray<ComplianceCopyItem> = [
    {
        // SEP-256 request 3. The firm gave the definition, and the line breaks
        // are part of it: the two criteria are a list, and a reader must be
        // able to read them apart. Every surface prints the body with
        // `whitespace-pre-line`.
        scope: 'hnw',
        code: 'definition',
        title: 'High net worth definition',
        confirmedByFirm: true,
        body: `A client qualifies as a High Net Worth Investor if, in the last financial year, they had either:
- an annual income of £100,000 or more; or
- net assets of £250,000 or more.
Net assets do not include the client's primary residence, pension (or pension withdrawals), or rights under qualifying contracts of insurance. Income does not include one-off pension withdrawals.`,
        options: [],
    },
    {
        // SEP-256 request 2. The definition sits under the conflicts question,
        // so the person who answers it reads what a conflict is.
        scope: 'conflict',
        code: 'definition',
        title: 'What a conflict of interest is',
        confirmedByFirm: true,
        body: 'A conflict may arise where Dowgate Wealth gains a benefit and there is a possible disadvantage to a client, or where one client may gain or avoid a loss with an associated possible loss to another client.',
        options: [],
    },
    {
        scope: 'conflict',
        code: 'categories',
        title: 'Conflict of interest categories',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} This list is a draft. You can still record the answer. Compliance will publish the approved list.`,
        options: [
            'Personal relationship',
            'Financial interest',
            'Gift or hospitality',
            'Outside business interest',
            'Related party transaction',
            'Other',
        ],
    },
    {
        scope: 'classification',
        code: 'retail',
        title: 'Retail client explanation',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} You can still record the classification. Compliance will publish the explanation of a retail client.`,
        options: [],
    },
    {
        scope: 'classification',
        code: 'professional',
        title: 'Professional client explanation',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} You can still record the classification. Compliance will publish the explanation of a professional client.`,
        options: [],
    },
    {
        scope: 'classification',
        code: 'elective_professional',
        title: 'Elective professional client explanation',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} You can still record the classification. Compliance will publish the explanation of an elective professional client.`,
        options: [],
    },
    {
        scope: 'classification',
        code: 'eligible_counterparty',
        title: 'Eligible counterparty explanation',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} You can still record the classification. Compliance will publish the explanation of an eligible counterparty.`,
        options: [],
    },
    {
        // SEP-263 decision D3 and request SEP-263.44. The question bank at
        // `packages/contract/src/client-application-questions.ts` IS the code
        // placeholder: it carries the draft wording of the supplied form, and
        // this item states that the wording is draft. No row seeds it, because
        // a row reads as approved wording that nobody approved.
        scope: 'application',
        code: 'form_wording',
        title: 'Client application form wording',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} The footer of every page of the DGW Application Form (Disc/Adv) Individual v2.0 reads "DRAFT (pending compliance sign-off)". Every question label, option and explanation of the client application comes from that draft form. Compliance will publish the approved wording.`,
        options: [],
    },
    {
        // SEP-263 decision D3 and request SEP-263.39. The declarations of Part
        // 12 are the wording a client accepts, so Compliance owns them apart
        // from the rest of the form.
        scope: 'application',
        code: 'declarations',
        title: 'Client application declarations (Part 12)',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} Part 12 of the DGW Application Form (Disc/Adv) Individual v2.0 holds the declarations, and that form is a draft. The platform carries the draft statements so that the structure is complete, and the Schedule of Charges line of declaration 6c is removed. Compliance will publish the approved declarations.`,
        options: [],
    },
    {
        scope: 'declaration',
        code: 'client_declaration',
        title: 'Client declaration scope',
        confirmedByFirm: false,
        body: `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} You can still send the signature pack. Compliance will publish the final wording of the client declaration.`,
        options: [],
    },
];

/** Find one item of the closed set, or undefined when the pair is unknown. */
export function complianceCopyItem(
    scope: ComplianceCopyScope,
    code: string,
): ComplianceCopyItem | undefined {
    return COMPLIANCE_COPY_ITEMS.find((item) => item.scope === scope && item.code === code);
}

/**
 * THE ONE READ of the seam.
 *
 * It returns the ACTIVE published entry of the `(scope, code)` pair, and it
 * returns the wording of the code when the register holds none. That wording is
 * a placeholder for an unconfirmed item, and the words of the firm for a
 * confirmed one.
 *
 * An entry is active when `supersededAt` is null. Where two entries claim to be
 * active, which is a fault that `PublishComplianceCopy` repairs on its next
 * write, the newest `activeFrom` wins, so a reader never sees the older words.
 *
 * The result is DERIVED, and nothing stores it. That is the rule that
 * `suitability-assessment-compute.ts` sets: store the raw inputs, and compute
 * every derived value once, so two surfaces cannot disagree.
 */
export function resolveComplianceCopy(
    scope: ComplianceCopyScope,
    code: string,
    entries: ReadonlyArray<ComplianceCopyEntryDto>,
): ResolvedComplianceCopy {
    const item = complianceCopyItem(scope, code);
    const active = entries
        .filter((e) => e.scope === scope && e.code === code && e.supersededAt === null)
        .sort((a, b) => b.activeFrom.localeCompare(a.activeFrom))[0];
    if (active) {
        return {
            scope,
            code,
            title: active.title,
            body: active.body,
            options: active.options ?? [],
            entryId: active.id,
            activeFrom: active.activeFrom,
            isPlaceholder: false,
        };
    }
    return {
        scope,
        code,
        title: item?.title ?? code,
        // An unknown pair is a caller fault, and the answer says so rather than
        // returns an empty string that reads as "there is no wording".
        body:
            item?.body ??
            `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} No item of the copy register carries the scope ${scope} and the code ${code}.`,
        options: [...(item?.options ?? [])],
        entryId: null,
        activeFrom: null,
        // An item that the firm confirmed is NOT a placeholder, although
        // Compliance has published nothing for it. An unknown pair has no item,
        // and it stays a placeholder.
        isPlaceholder: !item?.confirmedByFirm,
    };
}

/** Resolve every item of the closed set against one published register. */
export function resolveAllComplianceCopy(
    entries: ReadonlyArray<ComplianceCopyEntryDto>,
): ResolvedComplianceCopy[] {
    return COMPLIANCE_COPY_ITEMS.map((item) =>
        resolveComplianceCopy(item.scope, item.code, entries),
    );
}
